import { useRef, useState } from 'react';
import { reportBug } from '../api/bugs';
import { uploadTaskAttachment } from '../api/tasks';
import { MAX_ATTACH_BYTES, formatSize, fileToBase64 } from '../tasks/ukoly-shared';
import { ApiError } from '../api/client';
import { Bug, Lightbulb } from 'lucide-react';

export function BugReportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [type, setType] = useState<'bug' | 'rozvoj'>('bug');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ bugId: number; taskId: number } | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadWarn, setUploadWarn] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  if (!open) return null;
  const url = window.location.href;

  function reset() {
    setType('bug');
    setDescription('');
    setError(null);
    setDone(null);
    setFiles([]);
    setUploadWarn(null);
  }

  function addFiles(picked: FileList | null) {
    if (!picked?.length) return;
    const vybrane = [...picked];
    const velke = vybrane.filter((f) => f.size > MAX_ATTACH_BYTES);
    setUploadWarn(velke.length ? `Příliš velké soubory (max ${formatSize(MAX_ATTACH_BYTES)}): ${velke.map((f) => f.name).join(', ')}` : null);
    const prijate = vybrane.filter((f) => f.size <= MAX_ATTACH_BYTES);
    if (prijate.length) setFiles((prev) => [...prev, ...prijate]);
    if (fileInput.current) fileInput.current.value = '';
  }
  function close() {
    reset();
    onClose();
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const res = await reportBug({ url, type, description: description.trim() });
      const neulozene: string[] = [];
      for (const f of files) {
        try {
          const contentBase64 = await fileToBase64(f);
          await uploadTaskAttachment(res.taskId, {
            originalFilename: f.name,
            contentBase64,
            mimeType: f.type || null,
          });
        } catch {
          neulozene.push(f.name);
        }
      }
      setUploadWarn(neulozene.length ? `Nepodařilo se nahrát: ${neulozene.join(', ')}` : null);
      setDone(res);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Odeslání selhalo');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div
        className="modal fade show bug-report-layer"
        style={{ display: 'block' }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="bug-report-title"
      >
        <div className="modal-dialog modal-dialog-centered bug-report-dialog">
          <div className="modal-content bug-report-modal">
            <div className="modal-header">
              <div>
                <div className="bug-report-eyebrow">PODPORA PLATFORM 1</div>
                <h5 className="modal-title" id="bug-report-title">Nahlásit bug / námět na rozvoj</h5>
              </div>
              <button type="button" className="btn-close" aria-label="Zavřít" onClick={close}></button>
            </div>
            <div className="modal-body">
              {done ? (
                <div className="alert alert-success mb-0">
                  Díky! {type === 'bug' ? 'Bug' : 'Námět'} #{done.bugId} byl zaznamenán a vytvořen úkol
                  jako navazující úkol #{done.taskId}.
                  {uploadWarn && <div className="mt-2">{uploadWarn}</div>}
                </div>
              ) : (
                <form id="bug-report-form" onSubmit={submit}>
                  <div className="mb-3">
                    <label className="form-label">Typ</label>
                    <div className="bug-report-types">
                      <div className={`form-check bug-report-type${type === 'bug' ? ' active' : ''}`}>
                        <input className="form-check-input" type="radio" id="bug-t-bug" name="bug-type"
                          checked={type === 'bug'} onChange={() => setType('bug')} />
                        <label className="form-check-label" htmlFor="bug-t-bug"><Bug aria-hidden="true" /> Bug (něco nefunguje)</label>
                      </div>
                      <div className={`form-check bug-report-type${type === 'rozvoj' ? ' active' : ''}`}>
                        <input className="form-check-input" type="radio" id="bug-t-rozvoj" name="bug-type"
                          checked={type === 'rozvoj'} onChange={() => setType('rozvoj')} />
                        <label className="form-check-label" htmlFor="bug-t-rozvoj"><Lightbulb aria-hidden="true" /> Námět na rozvoj</label>
                      </div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Stránka</label>
                    <input className="form-control form-control-sm" value={url} disabled />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Popis</label>
                    <textarea className="form-control" rows={5} value={description} required
                      placeholder="Co se stalo / co bys chtěl(a)? Čím konkrétnější, tím lépe."
                      onChange={(e) => setDescription(e.target.value)} />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Přílohy</label>
                    <div
                      className="bug-report-drop"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
                    >
                      <input
                        ref={fileInput}
                        type="file"
                        multiple
                        className="d-none"
                        onChange={(e) => addFiles(e.target.files)}
                      />
                      <button type="button" className="btn btn-outline-secondary btn-sm" style={{ textTransform: 'none' }} onClick={() => fileInput.current?.click()}>
                        Vybrat soubory
                      </button>
                      <span className="bug-report-drop-hint">nebo sem přetáhni snímek obrazovky</span>
                    </div>
                    {files.length > 0 && (
                      <ul className="bug-report-files">
                        {files.map((f, i) => (
                          <li key={`${f.name}-${i}`}>
                            <span className="bug-report-file-name">{f.name}</span>
                            <span className="bug-report-file-size">{formatSize(f.size)}</span>
                            <button type="button" className="btn-close btn-close-sm" aria-label={`Odebrat ${f.name}`}
                              onClick={() => setFiles((prev) => prev.filter((_, j) => j !== i))} />
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {uploadWarn && <div className="alert alert-warning py-2 mb-2">{uploadWarn}</div>}
                  {error && <div className="alert alert-danger py-2 mb-0">{error}</div>}
                </form>
              )}
            </div>
            <div className="modal-footer">
              {done ? (
                <button className="btn btn-primary" style={{ textTransform: 'none' }} onClick={close}>Hotovo</button>
              ) : (
                <>
                  <button type="button" className="btn btn-outline-secondary" style={{ textTransform: 'none' }} onClick={close}>Zrušit</button>
                  <button type="submit" form="bug-report-form" className="btn btn-primary" style={{ textTransform: 'none' }}
                    disabled={busy || description.trim().length < 5}>
                    {busy ? (files.length ? 'Odesílám a nahrávám…' : 'Odesílám…') : 'Odeslat'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show bug-report-backdrop"></div>
    </>
  );
}
